"use client";

import { Analytics } from "@vercel/analytics/react";
import { SessionProvider, useSession } from "next-auth/react";
import type { Session } from "next-auth";
import { ThemeProvider, useTheme } from "next-themes";
import { createContext, type Dispatch, type ReactNode, type SetStateAction, useEffect } from "react";
import { Toaster } from "sonner";
import { useCreditsStore } from "@/components/default/credits";
import { NotificationProvider } from "@/components/default/notifications/NotificationProvider";
import { useSubscriptionStore } from "@/components/default/subscription/stores";
import { TestingProvider } from "@/components/scriba/mock";
import { TooltipProvider } from "@/components/tailwind/ui/tooltip";
import { IframeThemeOverride } from "@/components/tailwind/utils/IframeThemeOverride";

export const AppContext = createContext<{
  font: string;
  setFont: Dispatch<SetStateAction<string>>;
}>({
  font: "Default",
  setFont: () => { },
});

const ToasterProvider = () => {
  const { theme } = useTheme() as {
    theme: "light" | "dark" | "system";
  };
  return <Toaster theme={theme} />;
};

const AppInitializer = () => {
  const { data: session, status } = useSession();
  const { fetchCredits, hasFetchedCredits } = useCreditsStore();
  const { fetchSubscription, hasFetched: hasFetchedSubscription } = useSubscriptionStore();

  useEffect(() => {
    // Skip initialization if we're still loading the session
    if (status === "loading") {
      return;
    }

    const initializeApp = async () => {
      // Only initialize when we have confirmed authentication
      if (status === "authenticated" && session?.user) {
        // Initialize credits if not already fetched (global, regardless of current page)
        if (!hasFetchedCredits) {
          await fetchCredits(session.userId);
        }

        // Initialize subscription if not already fetched (global, regardless of current page)
        if (!hasFetchedSubscription) {
          await fetchSubscription();
        }
      } else if (status === "unauthenticated") {
        // User is not authenticated, set credits to 0
        useCreditsStore.setState({
          credits: { current: 0 },
          hasFetchedCredits: true,
          isLoading: false,
        });
      }
    };

    initializeApp();
  }, [status, session, hasFetchedCredits, hasFetchedSubscription, fetchCredits, fetchSubscription]);

  return null;
};

interface ProvidersProps {
  children: ReactNode;
  session?: Session | null;
}

export default function Providers({ children, session }: ProvidersProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <SessionProvider
        session={session}
        refetchOnWindowFocus={false}
        refetchInterval={30 * 60} // Refetch every 30 minutes
        refetchWhenOffline={false}
      >
        <ThemeProvider attribute="class" enableSystem disableTransitionOnChange defaultTheme="system">
          <IframeThemeOverride />
          <AppContext.Provider
            value={{
              font: "Default",
              setFont: () => { },
            }}
          >
            <TestingProvider>
              <NotificationProvider />
              <AppInitializer />
              <ToasterProvider />
              {children}
              <Analytics />
            </TestingProvider>
          </AppContext.Provider>
        </ThemeProvider>
      </SessionProvider>
    </TooltipProvider>
  );
}
